import { useCallback, useState } from "react";
import { noop } from "es-toolkit/function";
import AiAssistantChatContext from "../AiAssistantChatContext/AiAssistantChatContext";
import { useAiAssistantChatCurrentContext } from "../AiAssistantChatContext/AiAssistantChatContext.hooks";
import ChatInput from "../ChatInput/ChatInput";
import type { ChatInputAreaProps } from "./ChatInputArea.types";
import Placeholder from "./components/ChatInputAreaPlaceholder/ChatInputAreaPlaceholder";
import { ChatInputAreaSendButton } from "./components/ChatInputAreaSendButton/ChatInputAreaSendButton";
import { ChatInputAreaStopButton } from "./components/ChatInputAreaStopButton/ChatInputAreaStopButton";

export default function ChatInputArea({
  onSubmit,
  onStop = noop,
  isLoading = false,
  inputRef,
  showContext = false,
}: ChatInputAreaProps) {
  const [value, setValue] = useState("");
  const currentPageContext = useAiAssistantChatCurrentContext();
  const hasValue = value.trim().length > 0;
  const isActive = hasValue && !isLoading;

  const submitMessage = useCallback(() => {
    const trimmed = value.trim();
    if (!trimmed || isLoading) return;
    void onSubmit(trimmed);
    inputRef?.current?.clear();
  }, [value, isLoading, onSubmit, inputRef]);

  return (
    <div className="pt-3">
      {showContext && (
        <div className="pb-1">
          <AiAssistantChatContext {...currentPageContext} />
        </div>
      )}
      <div
        data-test-id="ai-toolbox-editor"
        className="flex items-end gap-2 py-1 px-2 rounded border border-neutral-light focus-within:border-primary"
      >
        <ChatInput
          ref={inputRef}
          hasValue={hasValue}
          onChange={setValue}
          onSubmit={submitMessage}
          isLoading={isLoading}
          placeholder={<Placeholder />}
        />
        {isLoading ? (
          <ChatInputAreaStopButton onClick={onStop} />
        ) : (
          <ChatInputAreaSendButton
            onClick={submitMessage}
            disabled={!hasValue}
            isActive={isActive}
          />
        )}
      </div>
    </div>
  );
}
